import axios from 'axios';
import { unwrapApiResponse, isNewApiFormat } from './unwrapApiResponse';

/**
 * Creates an authenticated axios instance
 * @param {string} idToken - The Auth0 ID token from user context
 * @returns {AxiosInstance} Configured axios instance with auth headers
 */
export const createAuthenticatedClient = (idToken) => {
  const client = axios.create({
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  // Add response interceptor to unwrap new API format and handle errors
  client.interceptors.response.use(
    (response) => {
      // Check if the response data is in the new wrapped format
      if (response.data && isNewApiFormat(response.data)) {
        try {
          // Unwrap the response data
          const unwrappedData = unwrapApiResponse(response.data);
          // Return a new response object with unwrapped data
          return {
            ...response,
            data: unwrappedData,
            // Preserve original wrapped response for debugging if needed
            _originalResponse: response.data,
          };
        } catch (err) {
          // unwrapApiResponse threw an error (API returned isSuccess: false)
          const error = new Error(err.message || 'API request failed');
          error.response = {
            ...response,
            data: response.data.error || { message: err.message },
            status: err.status || response.status,
          };
          error.code = err.code;
          error.meta = err.meta;
          return Promise.reject(error);
        }
      }
      // Not in new format, return as-is
      return response;
    },
    (error) => {
      // Handle HTTP errors
      if (error.response?.status === 401) {
        console.error('Unauthorized request - token may be expired');
      }

      // Check if error response body is in new format
      if (error.response?.data && isNewApiFormat(error.response.data)) {
        const apiError = error.response.data.error || {};
        error.message = apiError.message || error.message;
        error.code = apiError.code;
        error.debugMessage = apiError.debugMessage;
        error.packages = apiError.packages;
      }

      return Promise.reject(error);
    }
  );

  return client;
};

/**
 * Helper to get auth headers object
 * @param {string} idToken - The Auth0 ID token
 * @returns {Object} Headers object with Authorization
 */
export const getAuthHeaders = (idToken) => ({
  'Authorization': `Bearer ${idToken}`,
  'Content-Type': 'application/json',
});
